import React from "react";
import { METRIC_DEFINITION_MAP_CONFIG } from "../../config/metric-definition";
import { TDateTimeMode } from "../../types";
import { Tooltip } from "../ui-component/tooltip/Tooltip";
import * as I from "./MultipleFlexListSlider.d";

const formatData = (data) => {
	const keys = Object.keys(data);
	const formatData = {};
	for (let i = 0; i < keys.length; i++) {
		const d = data[keys[i]];

		if (d != null) {
			if (d.toString() === "0" || !Number.isInteger(d)) {
				if (
					d.toString().indexOf(".") !== -1 &&
					d.toString().indexOf(":") === -1
				) {
					formatData[keys[i]] = d.toFixed(2);
				} else {
					formatData[keys[i]] = d;
				}
			} else {
				formatData[keys[i]] = d;
			}
		}
	}
	return formatData;
};

const textHandler = (isStartDateAndCurrentDataSame: boolean) => {
	let help_text = "";
	if (isStartDateAndCurrentDataSame) {
		help_text = "(Plan values are for running styles)";
	} else {
		help_text = "(Plan values are for last worked style)";
	}
	return help_text;
};

const calculateExtraData = (type, data, timeMode: TDateTimeMode) => {
	if (type === "efficiency") {
		let hit_rate = 0;
		let act_eff = 0;
		let plan_eff = 0;
		let act_sah = 0;
		let plan_sah = 0;

		if (data["act_efficiency"]) {
			act_eff = data["act_efficiency"];
		}
		if (data["tEFF"]) {
			plan_eff = data["tEFF"];
		}
		if (act_eff > 0 && plan_eff > 0) {
			hit_rate = (act_eff / plan_eff) * 100;
		}
		data["hit_rate"] = hit_rate;
		const threshold = act_eff - plan_eff;
		data["act_plan_eff"] = threshold;

		if (data["sah"]) {
			act_sah = data["sah"];
		}
		if (data["tSAH"]) {
			plan_sah = data["tSAH"];
		}
		if (
			timeMode === "CUSTOM" ||
			timeMode === "MONTH" ||
			timeMode === "WEEK" ||
			timeMode === "OTHER" ||
			timeMode === "ONE_DAY"
		) {
			data["rt_sah"] = "--";
		}
		const sah_threshold = act_sah - plan_sah;
		data["act_plan_sah"] = sah_threshold;

		// this.logger.log("data====>", data);
	}
	return data;
};

const updateThreshold = (threshold, upper, lower, data) => {
	const threshold_value = data[threshold];
	if (threshold_value < 0) {
		return lower;
	} else if (threshold_value > 0) {
		return upper;
	} else {
		return "";
	}
};

const updateColors = (
	type,
	indicator,
	value,
	threshold,
	upper,
	lower,
	data
) => {
	if (value) {
		if (type === "plus_minus") {
			if (indicator) {
				const threshold_value = data[threshold];
				if (threshold_value) {
					if (threshold_value < 0) {
						return lower;
					} else {
						return upper;
					}
				} else {
					return lower;
				}
			} else {
				return "";
			}
		} else {
			if (indicator) {
				if (value) {
					if (value > threshold) {
						return upper;
					} else if (value < threshold) {
						return lower;
					}
				} else {
					return lower;
				}
			} else {
				return "";
			}
		}
	}
};

const addUnit = (value, percentage, pcs) => {
	if (percentage && value) {
		return "%";
	} else if (pcs && value) {
		return "pcs";
	} else {
		return "";
	}
};

const removeMinus = (percentage, type, key, data) => {
	if (type === "plus_minus") {
		if (data[key]) {
			if (data[key] < 0) {
				const data_string = data[key].toString();
				if (percentage) {
					return data_string.replace(/-/g, "") + "%";
				} else {
					return data_string.replace(/-/g, "");
				}
			} else {
				if (percentage) {
					return data[key] + "%";
				} else {
					return data[key];
				}
			}
		} else {
			if (percentage) {
				return 0 + "%";
			} else {
				return 0;
			}
		}
	} else if (type === "threshold_value") {
		return key;
	}
};

const removeMinusInCalculatedValue = (value) => {
	const data_string = value.toString();
	if (value < 0) {
		return data_string.replace(/-/g, "");
	} else {
		return data_string;
	}
};

const getProcessedData = (
	initialData: any,
	optionType: string,
	isStartDateAndCurrentDataSame: boolean,
	timeMode: TDateTimeMode
) => {
	const defaultEmptyData = {
		plan_operators: "--",
		plan_helpers: "--",
		plan_ironman: "--",
		plan_qc: "--",
		plan_total: "--",
		actual_operators: "--",
		actual_helpers: "--",
		actual_ironman: "--",
		actual_qc: "--",
		actual_total: "--",
	};
	let processedData = {};
	let help_text = "";
	if (initialData && initialData.data && initialData.data.length) {
		const data = calculateExtraData(
			optionType,
			initialData.data[0],
			timeMode
		);
		if (optionType === "man_power") {
			if (timeMode === "TODAY" || timeMode === "ONE_DAY") {
				processedData = data;
			} else {
				processedData = defaultEmptyData;
			}
		} else {
			processedData = formatData(data);
		}
	}

	help_text = textHandler(isStartDateAndCurrentDataSame);
	return [processedData, help_text];
};

const isValid = (value) => {
	return !!value;
};

const QualityStatsBlock: React.FC<I.TQualityStatsBlockProps> = ({
	data,
	_option,
}) => {
	return (
		<div className="stat-block__container">
			{_option.columns.map((item) => {
				return (
					<div className="stat-block stat-block__item">
						<div className="stat-lbl">
							<Tooltip
								metricDefinition={METRIC_DEFINITION_MAP_CONFIG.get(
									item.prop
								)}
							>
								<span>{item.name}</span>
							</Tooltip>
						</div>
						<div
							className={`stat-vlu-1
                                    ${updateColors(
										item.threshold_type,
										item.indicator,
										data[item.prop],
										item.threshold,
										item.upper_threshold_color,
										item.lower_threshold_color,
										data
									)}`}
						>
							<span className="l1">
								{data[item.prop] || "--"}
								{addUnit(
									data[item.prop],
									item.percentage,
									item.pcs
								)}
							</span>
							{isValid(item.indicator) &&
								isValid(data[item.prop]) && (
									<>
										<span
											style={{
												fontSize: "14px",
												marginRight: "-4px",
											}}
										>
											{" ( "}
										</span>
										<div className="ioc-green-ind"></div>
										<div className="ioc-red-ind"></div>
										<span className="l2">
											{data[item.prop]
												? removeMinusInCalculatedValue(
														(
															data[item.prop] -
															item.threshold
														).toFixed(2)
												  )
												: "--"}
											{isValid(item.indicator_suffix) && (
												<span>
													{item.indicator_suffix}
												</span>
											)}
											{" ) "}
										</span>
									</>
								)}
						</div>
						{isValid(item.sub) && (
							<div className="stat-vlu-2">
								{item.subname} {" : "}
								{data[item.subprop] || "--"}
							</div>
						)}
						{isValid(item.subFraction) && (
							<div className="stat-vlu-2">
								{item.subname} {" : "}:
								{data[item.numerator] || "--"}/
								{data[item.denominator] || "--"}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

const EfficiencyStatsBlock: React.FC<I.TEfficiencyStatsBlockProps> = ({
	data,
	_option,
}) => {
	return (
		<div className="stat-block__container">
			{_option.columns.map((item) => {
				return (
					<div className="stat-block stat-block__item">
						<div className="stat-lbl">
							<Tooltip
								metricDefinition={METRIC_DEFINITION_MAP_CONFIG.get(
									item.prop
								)}
							>
								<span>{item.name}</span>
							</Tooltip>
						</div>
						<div
							className={`stat-vlu-1 
                                    ${updateColors(
										item.threshold_type,
										item.indicator,
										data[item.prop],
										item.threshold,
										item.upper_threshold_color,
										item.lower_threshold_color,
										data
									)}`}
						>
							<span className="l1">
								{data[item.prop] || "--"}
								{addUnit(
									data[item.prop],
									item.percentage,
									item.pcs
								)}
							</span>
							{isValid(item.threshold_visibility) &&
								isValid(data[item.prop]) && (
									<span
										style={{
											marginLeft: "4px",
											marginRight: "-4px",
											fontSize: "14px",
										}}
									>
										{" ( "}
									</span>
								)}
							{isValid(item.indicator) &&
								isValid(item.threshold_visibility) &&
								isValid(data[item.prop]) && (
									<>
										<div className="ioc-green-ind"></div>
										<div className="ioc-red-ind"></div>
									</>
								)}
							{isValid(item.indicator) && (
								<span
									className={`l2
                                        ${updateThreshold(
											item.threshold,
											item.upper_threshold_color,
											item.lower_threshold_color,
											data
										)}`}
								>
									{isValid(item.threshold_visibility) &&
										isValid(!data[item.prop]) && (
											<>
												<span
													style={{
														marginLeft: "4px",
														marginRight: "-4px",
													}}
												>
													{" ( "}
												</span>
												{data[item.threshold] < 0 && (
													<span className="ioc-red-ind"></span>
												)}
												{data[item.threshold] > 0 && (
													<span className="ioc-green-ind"></span>
												)}
											</>
										)}

									{item.threshold_visibility
										? removeMinus(
												item.percentage,
												item.threshold_type,
												item.threshold,
												data
										  ) + " ) "
										: ""}
								</span>
							)}
						</div>
						{isValid(item.sub) && (
							<div className="stat-vlu-2">
								{item.subname}:{data[item.subprop] || "--"}
								{addUnit(
									data[item.subprop],
									item.percentage,
									item.pcs
								)}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

const ManPowerStatsBlock: React.FC<I.TManPowerStatsBlockProps> = ({
	data,
	_option,
}) => {
	return (
		<>
			<div className="upper-row row">
				<div className="stat-block no-padding">
					<div className="stat-lbl">
						<Tooltip
							metricDefinition={METRIC_DEFINITION_MAP_CONFIG.get(
								_option.main.prop
							)}
						>
							<span>{_option.main["name"]}</span>
						</Tooltip>
					</div>

					<div className="stat-vlu-1">
						<span className="l1">
							{data[_option.main.prop] || "--"}
						</span>
					</div>

					<div className="stat-vlu-2">
						{_option.main["subname"]}:
						{data[_option.main["subprop"]] || "--"}
					</div>
				</div>
			</div>
			<div className="stat-block__container">
				{_option.columns.map((item) => {
					return (
						<div className="stat-block stat-block__item">
							<div className="stat-lbl">
								<Tooltip
									metricDefinition={METRIC_DEFINITION_MAP_CONFIG.get(
										item.prop
									)}
								>
									<span>{item.name}</span>
								</Tooltip>
							</div>
							<div className="stat-vlu-1">
								<span className="l1">
									{data[item.prop] || "--"}
								</span>
							</div>
							<div className="stat-vlu-2">
								{item.subname}:{data[item.subprop] || "--"}
							</div>
						</div>
					);
				})}
			</div>
		</>
	);
};

const RenderStatsBlock: React.FC<I.IStatBlockProps> = ({ data, _option }) => {
	switch (_option.type) {
		case "quality":
			return <QualityStatsBlock data={data} _option={_option} />;
		case "efficiency":
			return <EfficiencyStatsBlock data={data} _option={_option} />;
		case "man_power":
			return <ManPowerStatsBlock data={data} _option={_option} />;
		default:
			return <></>;
	}
};

const MultipleFlexListSlider: React.FC<I.IMultipleFlexListSliderProps> = ({
	config,
	data,
	timeMode,
}) => {
	const _option = config.args.options;
	const [processedData, help_text] = getProcessedData(
		data.data,
		_option.type,
		data.isStartDateAndCurrentDataSame || false,
		timeMode
	);

	return (
		<div className="qs-card-wrap">
			<div className="z-qs-widget">
				<div className="qa-card-hdr">
					<div className="flx-1">
						{_option.type === "quality"
							? `${_option.heading} | ${_option.name} : ${
								processedData[_option.prop] || "--"
							  }`
							: `${_option.heading} `}
						{_option.type === "man_power" && help_text}
					</div>
				</div>
				<div className="stat-wrap">
					<RenderStatsBlock data={processedData} _option={_option} />
				</div>
			</div>
		</div>
	);
};

export default MultipleFlexListSlider;
